import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

type CheckboxTokens = {
  border: string
  checkedBg: string
  checkedBorder: string
  checkIcon: string
  disabledText: string
  focusRing: string
  label: string
}

// The tick is a data URI, so its colour can't come from a token reference.
// Strokes contrast with the checked fill per scheme: text/on-accent
// (darkBlue.900) on signal yellow, white on the legacy teal.
const checkIcon = (stroke: string) =>
  `url("data:image/svg+xml,%3Csvg width='10' height='8' viewBox='0 0 10 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 4L3.5 6.5L9 1' stroke='${encodeURIComponent(
    stroke
  )}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`

// Checked state carries the same brand accent as the Radio's on state
// (interactive/radio-on) so every selection control signals with one color.
// Labels read as row content, so they take text/primary like the option rows
// that host them.
const tokens: Record<ColorScheme, CheckboxTokens> = {
  dark: {
    border: 'border.checkbox-off',
    checkedBg: 'interactive.radio-on',
    checkedBorder: 'interactive.radio-on',
    checkIcon: checkIcon('#000A0F'),
    disabledText: 'text.tertiary',
    focusRing: 'interactive.focus-ring',
    label: 'text.primary',
  },
  light: {
    border: 'light.border.checkbox-off',
    checkedBg: 'light.interactive.radio-on',
    checkedBorder: 'light.interactive.radio-on',
    checkIcon: checkIcon('#FFFFFF'),
    disabledText: 'light.text.tertiary',
    focusRing: 'light.interactive.focus-ring',
    label: 'light.text.primary',
  },
}

export const checkboxRootStyles = (disabled: boolean): SystemStyleObject => ({
  alignItems: 'center',
  cursor: disabled ? 'default' : 'pointer',
  display: 'inline-flex',
  gap: '8px',
  minW: 0,
})

export const checkboxInputStyles = (
  colorScheme: ColorScheme,
  disabled: boolean,
  dimWhenDisabled = true,
  boxBorderColor?: string
): SystemStyleObject => {
  const t = tokens[colorScheme]

  return {
    appearance: 'none',
    bgColor: 'transparent',
    border: '1.5px solid',
    borderColor: boxBorderColor ?? t.border,
    // Sharp corners — no exceptions (Checkbox spec).
    borderRadius: '0',
    cursor: disabled ? 'default' : 'pointer',
    flexShrink: 0,
    h: '14px',
    m: 0,
    w: '14px',
    _checked: {
      backgroundImage: t.checkIcon,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      bgColor: t.checkedBg,
      borderColor: t.checkedBorder,
    },
    _disabled: {
      cursor: 'default',
      opacity: dimWhenDisabled ? 0.55 : 1,
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: t.focusRing,
      outlineOffset: '2px',
    },
  }
}

export const checkboxLabelStyles = (
  colorScheme: ColorScheme,
  disabled: boolean,
  labelColor?: string
): SystemStyleObject => ({
  color:
    labelColor ??
    (disabled ? tokens[colorScheme].disabledText : tokens[colorScheme].label),
  m: 0,
  minW: 0,
  textStyle: 'caption',
})
